import { LuCircleEllipsis } from 'react-icons/lu';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { ApplicationCompletionStatus, ApplicationCompletionSection } from '~/types/application-completion';

interface Props {
    completionStatus?: ApplicationCompletionStatus | null;
    selectedSections: string[];
    onSectionToggle: (sectionKey: string) => void;
}

interface SectionInfo {
    key: string;
    label: string;
}

export const SECTION_INFO: Record<string, SectionInfo> = {
    profile: { key: 'profile', label: 'Client Info' },
    medpro: { key: 'medpro', label: 'Medical Professional' },
    insurance: { key: 'insurance', label: 'Insurance' },
    financial: { key: 'financial', label: 'Financial' },
    contact: { key: 'contact', label: 'Contact' },
    agreement: { key: 'agreement', label: 'Agreements' },
};

const StatusCards: React.FC<Props> = ({
    completionStatus,
    selectedSections,
    onSectionToggle,
}) => {
    const getSectionStyle = (section: ApplicationCompletionSection, isSelected: boolean) => {
        const baseStyle = {
            Icon: section.is_complete ? MdOutlineCheckCircle : LuCircleEllipsis,
            iconColor: section.is_complete ? 'text-[#AFC12D]' : 'text-[#FEBF3E]',
        };

        if (isSelected) {
            // Selected state - darker border and background
            return {
                ...baseStyle,
                borderColor: section.is_complete ? 'border-[#AFC12D]' : 'border-[#FEBF3E]',
                backgroundColor: section.is_complete ? 'bg-[#AFC12D]/10' : 'bg-[#FEB63E1A]',
                shadow: 'shadow-md',
            };
        }

        // Unselected state - lighter appearance
        return {
            ...baseStyle,
            borderColor: 'border-gray-200',
            backgroundColor: 'bg-white',
            shadow: 'shadow-sm',
        };
    };

    if (!completionStatus) {
        return (
            <div className='mb-7.5 flex items-center justify-center rounded-lg bg-white p-8'>
                <div className='text-gray-500'>
                    Loading application status...
                </div>
            </div>
        );
    }

    const completedCount = completionStatus.sections.filter(
        (s) => s.is_complete
    ).length;

    return (
        <div className='mb-7.5 rounded-lg bg-[#F8F8F8] p-6'>
            <div className='mb-4 flex items-center justify-between'>
                <h2 className='text-[22px]/[28px] font-bold'>
                    Application Status ({completedCount}/{completionStatus.sections.length} complete)
                </h2>
                {selectedSections.length > 0 && selectedSections.length < completionStatus.sections.length && (
                    <button
                        onClick={() => onSectionToggle('all')}
                        className='text-[14px]/[18px] font-bold uppercase text-[#1CB4C3] hover:text-[#159BA8] transition-colors duration-300'
                    >
                        Show All
                    </button>
                )}
            </div>

            <div className='grid grid-cols-12 gap-[16px]'>
                {completionStatus.sections.map((section) => {
                    const isSelected = selectedSections.includes(section.key);
                    const { Icon, borderColor, iconColor, backgroundColor, shadow } = getSectionStyle(section, isSelected);
                    const sectionInfo = SECTION_INFO[section.key];

                    return (
                        <button
                            key={section.key}
                            onClick={() => onSectionToggle(section.key)}
                            className={`col-span-12 rounded-lg border-2 ${borderColor} ${backgroundColor} ${shadow} 
                                transition-all duration-200 hover:scale-105 cursor-pointer
                                sm:col-span-6 md:col-span-4`}
                        >
                            <div className='p-[12px] text-left'>
                                <h3 className='text-[16px]/[24px] font-bold'>
                                    <Icon
                                        className={`mb-2 block ${iconColor}`}
                                        size={24}
                                    />
                                    {sectionInfo?.label || section.key}
                                </h3>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default StatusCards;

